import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

// Hoisted AnimatedNumber to ensure stable identity across HomePage re-renders
const AnimatedNumber = React.memo(({ to, duration = 1200, start = false }: { to: number; duration?: number; start?: boolean }) => {
  const [value, setValue] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (start && !hasRunRef.current) {
      setShouldAnimate(true);
    }
  }, [start]);

  useEffect(() => {
    if (!shouldAnimate || hasRunRef.current) return;
    hasRunRef.current = true;
    const startTime = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - startTime) / duration);
      setValue(Math.floor(progress * to));
      if (progress < 1) requestAnimationFrame(step);
    };
    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [shouldAnimate, to, duration]);

  return <>{value}</>;
});

const CategorySection = styled.section`
  padding: 80px 24px;
  text-align: center;
  position: relative;
  background: linear-gradient(180deg, #FFF9F4 0%, #FFFFFF 100%);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7S14.866 4 11 4 4 7.134 4 11s3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23d2a94d' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E");
    opacity: 0.3;
    pointer-events: none;
  }
`;

const CategorySlider = styled.div`
  width: 100%;
  overflow: hidden;
  margin-top: 40px;
  position: relative;
`;

const SliderWrapper = styled.div`
  display: flex;
  transition: transform 0.5s ease;
  will-change: transform;
`;

interface NavigationButtonProps {
  direction: 'prev' | 'next';
}

const NavigationButton = styled.button<NavigationButtonProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(212, 175, 55, 0.2);
  transition: all 0.3s ease;
  ${props => props.direction === 'prev' ? 'left: 10px;' : 'right: 10px;'}

  &:hover {
    background: white;
    box-shadow: 0 6px 16px rgba(212, 175, 55, 0.35);
  }

  svg {
    width: 24px;
    height: 24px;
    stroke: #333;
  }
`;

const CategoryCard = styled.div`
  background: white;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: transform 0.28s ease, box-shadow 0.28s ease;
  will-change: transform, box-shadow;
  flex: 0 0 280px;
  margin-right: 20px;
  margin-bottom: 24px;
  width: 280px;
  height: 380px; /* increased as requested */
  animation: fadeUp 0.6s ease both;

  /* Thin golden curved accent */
  position: relative;
  isolation: isolate;
  
  &::before {
    content: '';
    position: absolute;
    top: -30px;
    left: -30px;
    width: 160px;
    height: 160px;
    background: radial-gradient(100px 70px at 80px 70px, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.08) 40%, transparent 60%);
    transform: rotate(-12deg);
    z-index: 1;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-6px) scale(1.03);
    box-shadow: 0 12px 28px rgba(212, 175, 55, 0.28);
  }
`;

const CategoryImage = styled.img.attrs({ loading: 'lazy', decoding: 'async' })`
  width: 100%;
  height: 280px; /* keep image proportional to taller card */
  object-fit: cover;
  filter: contrast(104%) saturate(106%);
  transition: transform 0.32s ease;
  will-change: transform;
  ${CategoryCard}:hover & {
    transform: scale(1.05);
  }
`;

const CategoryName = styled.h3`
  color: var(--primary-purple);
  text-align: center;
  padding: 1rem 0.75rem 1.2rem;
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  font-family: 'Playfair Display', serif;
  text-decoration: none;
  position: relative;
  transition: transform 0.28s ease, color 0.28s ease;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 8px;
    transform: translateX(-50%);
    width: 40px;
    height: 2px;
    background: linear-gradient(to right, transparent, #D4AF37, transparent);
    border-radius: 2px;
    opacity: 0.8;
  }

  ${CategoryCard}:hover & {
    transform: translateY(-2px);
    color: var(--primary-purple);
  }
  
  &:hover {
    color: var(--primary-purple);
  }
  
  a {
    text-decoration: none;
    color: inherit;
  }
`;

// Ensure CategoryCard links have no underline
const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const HeroSection = styled.section`
  position: relative;
  background: url('/FF_CONVERR.jpg') no-repeat center center;
  background-size: cover;
  min-height: 80vh;
  display: flex;
  align-items: center;
  padding-top: 120px;
  animation: fadeIn 1s ease both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  color: white;
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

// Welcome + USP section (placed under hero)
const WelcomeSection = styled.section`
  background: linear-gradient(to bottom, #FFF8F0, #FFF5E6);
  padding: 4rem 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23d2a94d' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E");
    opacity: 0.5;
    z-index: 0;
  }
`;

const WelcomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem 4rem;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Header that spans both columns, centered like the reference image
const WelcomeHeader = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  margin-bottom: 1rem;
`;

// Gold gradient with shine animation
const WelcomeHeaderTitle = styled.h2`
  font-size: clamp(2rem, 4.5vw, 3rem);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0;
  background: linear-gradient(to right, #BF953F, #FCF6BA, #B38728, #FBF5B7, #AA771C);
  background-size: 200% auto;
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  animation: shine 3s linear infinite;
  text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  
  @keyframes shine {
    to {
      background-position: 200% center;
    }
  }
`;

const WelcomeImageWrap = styled.figure`
  margin: 0;
  border-radius: 12px;
  overflow: hidden;
  border: 4px solid #D4AF37;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1), 0 2px 10px rgba(212, 175, 55, 0.2);
  width: clamp(320px, 40vw, 560px);
  aspect-ratio: 4 / 3;
  justify-self: center;
  transform: perspective(1000px) rotateY(2deg) rotateX(1deg);
  transition: transform 0.5s ease, box-shadow 0.5s ease, opacity 0.6s ease, transform 0.6s ease;
  opacity: 0;

  &.revealed {
    opacity: 1;
    transform: perspective(1000px) rotateY(2deg) rotateX(0deg);
  }
  
  &:hover {
    transform: perspective(1000px) rotateY(0deg) rotateX(0deg);
    box-shadow: 0 15px 30px rgba(0,0,0,0.15), 0 2px 15px rgba(212, 175, 55, 0.3);
  }

  /* Subtle floating sparkles around the cake area */
  &::after {
    content: '';
    position: absolute;
    pointer-events: none;
    top: -12px;
    left: -12px;
    right: -12px;
    bottom: -12px;
    background-image:
      radial-gradient(circle at 10% 20%, rgba(212,175,55,0.15) 0 2px, transparent 3px),
      radial-gradient(circle at 80% 30%, rgba(212,175,55,0.12) 0 2px, transparent 3px),
      radial-gradient(circle at 35% 80%, rgba(212,175,55,0.1) 0 2px, transparent 3px);
    animation: floatSparkles 6s ease-in-out infinite;
  }

  @keyframes floatSparkles {
    0%, 100% { transform: translateY(0); opacity: 1; }
    50% { transform: translateY(-6px); opacity: 0.9; }
  }

  @media (max-width: 900px) {
    width: clamp(300px, 80vw, 520px);
  }
`;

const WelcomePhoto = styled.img.attrs({ loading: 'lazy', decoding: 'async' })`
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Move image slightly more downward within the frame */
  object-position: center 65%;
`;

const WelcomeText = styled.article`
  color: var(--primary-purple);
`;

const WelcomeTitle = styled.h2`
  margin: 0 0 0.5rem;
  letter-spacing: 0.5px;
  
  .script-font {
    font-family: 'Great Vibes', 'Betrycia-Regular', cursive;
    font-size: clamp(2.5rem, 5vw, 3.2rem);
    font-weight: 400;
    display: block;
    color: var(--primary-purple);
    margin-bottom: 0.2rem;
  }
  
  .serif-font {
    font-family: 'Playfair Display', 'Betrycia-Regular', serif;
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 700;
    display: block;
    color: var(--primary-purple);
  }
`;

const DecorativeDivider = styled.div`
  position: relative;
  width: 120px;
  height: 15px;
  margin: 1rem 0 1.5rem;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, transparent, #D4AF37, transparent);
    top: 50%;
    transform: translateY(-50%);
  }
  
  &::after {
    content: '❦';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: #D4AF37;
    font-size: 1rem;
  }
`;

const WelcomeParagraph = styled.p`
  color: #4a4a4a;
  line-height: 1.9;
  margin: 0 0 1.2rem;
  font-size: 1.05rem;
`;

const Tagline = styled.p`
  font-style: italic;
  color: var(--primary-purple);
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  font-family: 'Playfair Display', serif;
`;

const USPGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 10%;
    bottom: 10%;
    left: 50%;
    width: 1px;
    background: linear-gradient(to bottom, transparent, rgba(212, 175, 55, 0.5), transparent);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    
    &::before {
      display: none;
    }
  }
`;

const USPCard = styled.div`
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.8rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(212, 175, 55, 0.06);
    box-shadow: 0 8px 24px rgba(212, 175, 55, 0.2);
  }

  @media (max-width: 600px) {
    align-items: center;
    text-align: center;
  }
`;

const USPIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #D4AF37;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  box-shadow: 0 4px 12px rgba(212, 175, 55, 0.35);
  margin: 0.4rem 0;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px) scale(1.06);
    box-shadow: 0 6px 15px rgba(212, 175, 55, 0.45);
    filter: drop-shadow(0 0 6px rgba(212, 175, 55, 0.5));
  }
`;

const USPTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  color: var(--primary-purple);
  font-size: 1.15rem;
  text-transform: uppercase;
`;

const USPText = styled.p`
  margin: 0;
  color: #4a4a4a;
  line-height: 1.6;
`;

const FeaturedSection = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--primary-purple);
  text-align: center;
  margin-bottom: 1.6rem;
  letter-spacing: 0.8px;
  position: relative;
  animation: fadeUp 0.8s ease both;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -12px;
    transform: translateX(-50%);
    width: 90px;
    height: 3px;
    background: linear-gradient(to right, transparent, #D4AF37, transparent);
    border-radius: 3px;
    opacity: 0.95;
  }
`;

const CategoryTagline = styled.p`
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  color: var(--primary-purple);
  margin: 18px 0 28px;
  text-align: center;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  animation: fadeUp 0.6s ease both;
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
`;

// Contact Us Section Styles
// Animations for subtle motion and reveals
const floatAnim = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
  100% { transform: translateY(0); }
`;

const pulseSoft = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(103, 80, 164, 0.18); }
  70% { box-shadow: 0 0 0 12px rgba(103, 80, 164, 0); }
  100% { box-shadow: 0 0 0 0 rgba(103, 80, 164, 0); }
`;

const ContactSection = styled.section`
  background: white;
  padding: 4rem 2rem;
  animation: fadeIn 0.8s ease both;
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ContactHeading = styled.h2`
  font-size: clamp(1.8rem, 3.6vw, 2.6rem);
  color: var(--primary-purple);
  text-align: center;
  margin-bottom: 2.2rem;
  font-weight: 800;
  letter-spacing: 0.4px;
  animation: fadeUp 0.8s ease both;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.4rem;
  align-items: start;
  justify-items: center;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContactItem = styled.div`
  text-align: center;
  color: var(--primary-purple);
  animation: fadeUp 0.8s ease both;

  a {
    color: var(--primary-purple);
    text-decoration: none;
    transition: color 0.36s ease, text-shadow 0.36s ease, text-decoration-color 0.36s ease;
  }

  a:hover {
    color: var(--primary-yellow);
    text-shadow: 0 0 8px rgba(210, 169, 77, 0.35);
    text-decoration: underline;
    text-decoration-color: var(--primary-yellow);
    text-underline-offset: 4px;
    text-decoration-thickness: 2px;
  }
`;

const IconCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--primary-purple);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75rem;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  animation: ${floatAnim} 6s ease-in-out infinite;

  svg { width: 30px; height: 30px; color: #fff; }

  /* Hover glow per item (golden/yellow) */
  &.whatsapp:hover { box-shadow: 0 0 0 8px rgba(210, 169, 77, 0.16), 0 8px 24px rgba(210, 169, 77, 0.32); transform: translateY(-2px) scale(1.06); }
  &.location:hover  { box-shadow: 0 0 0 8px rgba(210, 169, 77, 0.16), 0 8px 24px rgba(210, 169, 77, 0.32); transform: translateY(-2px) scale(1.06); }
  &.email:hover     { box-shadow: 0 0 0 8px rgba(210, 169, 77, 0.16), 0 8px 24px rgba(210, 169, 77, 0.32); transform: translateY(-2px) scale(1.06); }

  /* Gentle pulse on load */
  &.whatsapp, &.location, &.email { animation: ${floatAnim} 6s ease-in-out infinite, ${pulseSoft} 5s ease-in-out infinite; }
`;
const ProductInfo = styled.div`
  padding: 1.5rem;
  
  h3 {
    color: var(--primary-purple);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #666;
  }
`;

// Quality Section redesign styled components
const QualitySection = styled.section`
  background: linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%);
  padding: 4rem 0;
  animation: fadeIn 0.8s ease both;
`;

const QualityContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 2rem;
  padding: 0 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 0 1rem;
  }
`;

const QualityText = styled.div`
  color: var(--primary-purple);
`;

const QualityHeading = styled.h2`
  color: var(--primary-purple);
  font-size: clamp(1.6rem, 3.2vw, 2.4rem);
  margin-bottom: 1rem;
  font-weight: 800;
  letter-spacing: 0.5px;
`;

const QualityParagraph = styled.p`
  margin-bottom: 1rem;
  line-height: 1.7;
  color: #4a4a4a;
  font-size: clamp(0.9rem, 1.8vw, 1rem);
`;

const OrderButton = styled.a`
  display: inline-block;
  background-color: var(--primary-yellow);
  color: var(--primary-purple);
  padding: 0.9rem 1.6rem;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 800;
  box-shadow: 0 8px 20px rgba(210, 169, 77, 0.35);
  transition: transform 180ms ease, box-shadow 180ms ease;
  will-change: transform, box-shadow;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(210, 169, 77, 0.5);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 6px 16px rgba(210, 169, 77, 0.4);
  }
`;

const QualityImageWrap = styled.div`
  position: relative;
  min-height: 420px;
  border-radius: 24px;
  overflow: hidden;
  isolation: isolate;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0.0), rgba(0,0,0,0.08));
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: 900px) {
    min-height: 320px;
  }
`;

const QualityBadge = styled.img.attrs({ loading: 'lazy', decoding: 'async' })`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 6px 22px rgba(0,0,0,0.2);
  z-index: 2;
  background: var(--primary-purple);
`;

const QualityPhotoMain = styled.img.attrs({ loading: 'lazy', decoding: 'async' })`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Slight upward bias to align focus within frame */
  object-position: center 40%;
  transform: scale(1.02);
  filter: contrast(105%) saturate(108%);
`;

const QualityPhotoSmall = styled.img.attrs({ loading: 'lazy', decoding: 'async' })`
  position: absolute;
  width: 180px;
  height: 180px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 12px 28px rgba(0,0,0,0.25);
  z-index: 2;

  &.top {
    top: 18px;
    right: 18px;
    transform: rotate(-2deg);
  }

  &.bottom {
    bottom: 18px;
    left: 18px;
    transform: rotate(2deg);
  }

  @media (max-width: 900px) {
    width: 120px;
    height: 120px;
  }
`;

// Instagram Reviews styled components
const InstagramSection = styled.section`
  background: #fff;
  padding: 4rem 2rem;
`;

const InstagramContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InstagramHeader = styled.div`
  padding: 0 0.5rem;
`;

const InstagramTitle = styled.h2`
  font-size: clamp(1.8rem, 3.6vw, 2.6rem);
  color: var(--primary-purple);
  margin-bottom: 1rem;
  font-weight: 800;
  letter-spacing: 0.4px;
`;

const InstagramLead = styled.p`
  color: #4a4a4a;
  line-height: 1.7;
`;

// Stats Section (purple background with counters)
const StatsSection = styled.section`
  background: var(--primary-purple);
  padding: 3.5rem 2rem;
`;

const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #fff;
  animation: fadeUp 0.6s ease both;
`;

const StatIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: var(--primary-yellow);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 6px 16px rgba(212, 175, 55, 0.35);
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.6px;
  color: rgba(255,255,255,0.9);
`;

const StatNumber = styled.span`
  font-size: clamp(1.6rem, 3.2vw, 2.2rem);
  font-weight: 900;
  color: #fff;
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ReviewCard = styled.div`
  background: var(--primary-purple);
  border-radius: 12px;
  padding: 1.5rem;
  position: relative;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  overflow: hidden;
`;

const QuoteCorner = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 86px;
  height: 86px;
  border-bottom-left-radius: 86px;
  background: var(--primary-yellow);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  z-index: 0;
  pointer-events: none;
`;

const ReviewName = styled.h3`
  font-size: 1rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 2;
  color: var(--primary-yellow);
`;

const ReviewText = styled.p`
  color: #fff;
  line-height: 1.7;
  position: relative;
  z-index: 2;
`;

const InstagramFallback = styled.p`
  text-align: center;
  color: #666;
`;

// Testimonial Section Styles
const TSection = styled.section`
  background: #ffffff;
  padding: 4rem 1rem;
`;

const TContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const THeadingScript = styled.div`
  font-family: 'Pacifico', cursive;
  color: var(--primary-purple); /* theme purple */
  font-size: clamp(1.4rem, 3.2vw, 2.2rem);
`;

const THeadingMain = styled.h2`
  font-weight: 900;
  letter-spacing: 1px;
  color: #000;
  text-transform: uppercase;
  font-size: clamp(1.6rem, 4.8vw, 2.6rem);
  margin: 0.25rem 0 0.75rem;
`;

const THeadingLine = styled.div`
  width: 70px;
  height: 4px;
  background: #f5b301; /* yellow accent */
  border-radius: 3px;
  margin: 0.75rem auto 1.8rem;
`;

const TViewport = styled.div`
  overflow: hidden;
  width: 100%;
`;

const TTrack = styled.div`
  display: flex;
  transition: transform 0.8s ease-in-out;
  will-change: transform;
`;

const TPage = styled.div`
  flex: 0 0 100%;
  display: flex;
  justify-content: center;
  gap: 1.5rem;
`;

const TCard = styled.div<{ active?: boolean }>`
  background: var(--primary-purple); /* theme purple */
  color: #fff;
  border-radius: 10px;
  padding: 1.2rem 1.2rem;
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  /* Remove border shadow by default; add glow on hover */
  box-shadow: none;
  transform: ${props => (props.active ? 'scale(1)' : 'scale(0.88)')};
  transition: all 0.6s ease-in-out;
  filter: ${props => (props.active ? 'none' : 'grayscale(0.7)')};
  opacity: ${props => (props.active ? 1 : 0.6)};
  min-height: 180px;
  box-sizing: border-box;
  margin: 0.5rem;

  &:hover {
    box-shadow: 0 0 18px rgba(107,75,154,0.55);
    transform: scale(1);
    filter: none;
    opacity: 1;
  }
  @media (max-width: 600px) {
    padding: 1.25rem;
    margin: 0; /* ensure full-width single card without side bleed */
    min-height: 200px;
  }
`;

const TAvatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
`;

const TCardBody = styled.div`
  text-align: left;
`;

const TClientName = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  text-transform: uppercase;
  margin-bottom: 0.15rem;
`;

/* Profession removed per requirements */

const TReviewText = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
`;

const TPagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 1.2rem;
`;

const TPaginationDot = styled.button<{ active?: boolean }>`
  width: ${p => (p.active ? '24px' : '18px')};
  height: 6px;
  border-radius: 6px;
  border: none;
  background: ${p => (p.active ? '#f5b301' : '#d1d1d1')};
  cursor: pointer;
  transition: background 0.2s ease, width 0.2s ease;
`;

const HomePage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef(null);
  const totalItems = 10; // Total number of category cards
  const [itemsToShow, setItemsToShow] = useState(3); // Responsive count
  const STEP = 300; // Card width + margin for translation
  const maxIndex = Math.max(0, totalItems - itemsToShow); // Prevent empty space

  // Testimonials data & slider state
  const testimonials = [
    { name: 'Amir Raza', text: 'Flour & Fancy always delivers fresh, flavorful treats — never disappoints!', avatar: 'https://i.pravatar.cc/80?img=11' },
    { name: 'Mehreen Faisal', text: 'The customization and taste are spot on. Highly recommend for events.', avatar: 'https://i.pravatar.cc/80?img=22' },
    { name: 'Usman Ali', text: 'Great service and delicious desserts. A weekly must-have for us.', avatar: 'https://i.pravatar.cc/80?img=33' },
    { name: 'Hira Khan', text: 'Super moist cakes and perfect frosting. Exactly what we wanted!', avatar: 'https://i.pravatar.cc/80?img=44' },
    { name: 'Bilal Ahmed', text: 'Affordable and high-quality. The brownies are a family favorite.', avatar: 'https://i.pravatar.cc/80?img=55' },
    { name: 'Sana Farooq', text: 'Beautiful presentation and taste — my guests loved every bite.', avatar: 'https://i.pravatar.cc/80?img=66' },
  ];

  const [tPerView, setTPerView] = useState(3);
  const tLen = testimonials.length;
  const [tIndex, setTIndex] = useState(tLen); // start at middle copy for seamless loop
  const [tEnableTransition, setTEnableTransition] = useState(true);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setTPerView(w >= 1024 ? 3 : w >= 600 ? 2 : 1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Total cards (used for per-card sliding)
  const tPageCount = tLen;

  // Auto-slide moves rightward showcasing each card smoothly.
  useEffect(() => {
    const id = setInterval(() => {
      setTIndex(prev => prev + 1);
    }, 5000);
    return () => clearInterval(id);
  }, [tPageCount, tPerView]);
  
  // Handle slide transition to ensure right card becomes main card
  const handleSlideChange = (newIndex: number) => {
    setTEnableTransition(true);
    setTIndex(tLen + newIndex);
  };

  // Handle transition end to reset index to middle copy for seamless looping
  const handleTTransitionEnd = () => {
    if (tIndex >= 2 * tLen) {
      setTEnableTransition(false);
      setTIndex(tLen);
      requestAnimationFrame(() => setTEnableTransition(true));
    }
  };

  // Scroll-reveal: reveal elements as they enter the viewport
  useEffect(() => {
    const elements = document.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLElement;
          const dir = el.getAttribute('data-reveal-dir');
          if (dir) {
            el.setAttribute('data-reveal', dir);
          }
          el.classList.add('revealed');
        }
      });
    }, { threshold: 0.15 });

    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Click-bounce helper
  const triggerBounce = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('click-bounce');
    void target.offsetWidth; // reflow to restart animation
    target.classList.add('click-bounce');
  };
  // Responsive calculation for itemsToShow based on container width
  useEffect(() => {
    const updateItems = () => {
      const containerWidth = (sliderRef.current as HTMLDivElement | null)?.clientWidth ?? window.innerWidth;
      const count = Math.max(1, Math.floor(containerWidth / STEP));
      setItemsToShow(count);
    };

    updateItems();
    window.addEventListener('resize', updateItems);
    return () => window.removeEventListener('resize', updateItems);
  }, []);

  // Function to handle manual navigation
  const handleSlide = (direction: 'prev' | 'next') => {
    if (direction === 'next') {
      setCurrentIndex(prev => (prev >= maxIndex) ? 0 : prev + 1);
    } else {
      setCurrentIndex(prev => (prev <= 0) ? maxIndex : prev - 1);
    }
  };

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev >= maxIndex) ? 0 : prev + 1);
    }, 5000); // 5 seconds interval

    return () => clearInterval(interval);
  }, [maxIndex]);

  // Stats counters: start when section enters viewport
  const statsRef = useRef<HTMLDivElement | null>(null);
  const [statsStart, setStatsStart] = useState(false);
  const statsHasAnimatedRef = useRef(false);
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !statsHasAnimatedRef.current) {
          setStatsStart(true);
          statsHasAnimatedRef.current = true;
          obs.disconnect();
        }
      });
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Inline AnimatedNumber removed; using module-scope AnimatedNumber defined above

  return (
    <>
      <HeroSection>
        <HeroContent>
          {/* Text removed as per mobile UI requirements */}
        </HeroContent>
      </HeroSection>

      {/* Welcome Section under hero */}
      <WelcomeSection>
        <WelcomeContainer>
          <WelcomeHeader>
            <WelcomeHeaderTitle>Welcome to Flour and Fancy</WelcomeHeaderTitle>
          </WelcomeHeader>
          <WelcomeImageWrap className="revealed" data-reveal="left">
            <WelcomePhoto src="/cake.jpg" alt="Signature cake from Flour & Fancy" />
          </WelcomeImageWrap>

          <WelcomeText className="revealed" data-reveal="right">
            <WelcomeTitle>
              <span className="script-font">Indulgence</span>
              <span className="serif-font">Begins Here</span>
            </WelcomeTitle>
            <DecorativeDivider />
            <Tagline>Crafted with passion, baked to perfection.</Tagline>
            <WelcomeParagraph>
              At Flour and Fancy, we believe every treat should bring joy. That is why we bake fresh every day using premium ingredients and a whole lot of love. Every bite is soft, flavorful and unforgettable.
            </WelcomeParagraph>

            <USPGrid>
              <USPCard>
                <USPIcon>
                  {/* Whisk icon (line-style) */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <ellipse cx="9" cy="9" rx="4.5" ry="3.5"></ellipse>
                    <path d="M13 13 L20 20"></path>
                    <path d="M7 9 L11 13"></path>
                    <path d="M11 5 L7 9"></path>
                  </svg>
                </USPIcon>
                <USPTitle>100% Healthy and Fresh</USPTitle>
                <USPText>
                  We bake with quality and cleanliness at the heart of everything. No compromises and no shortcuts. Just pure fresh goodness in every bite.
                </USPText>
              </USPCard>

              <USPCard>
                <USPIcon>
                  {/* Cupcake icon (line-style) */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {/* Base */}
                    <path d="M7 14 L8 19 H16 L17 14 Z"></path>
                    {/* Frosting */}
                    <path d="M9 12 L10.5 10.2 L12 9.2 L13.5 10.2 L15 12 H9 Z"></path>
                  </svg>
                </USPIcon>
                <USPTitle>Customizable Designs</USPTitle>
                <USPText>
                  From themed desserts to personalized celebration treats. Share your idea and we will turn it into a delicious masterpiece crafted just for you.
                </USPText>
              </USPCard>
            </USPGrid>
          </WelcomeText>
        </WelcomeContainer>
      </WelcomeSection>

      {/* Specialities section removed as requested */}
      
      {/* New Category Section with Slider */}
      <CategorySection>
        <SectionTitle data-reveal>Shop By Category</SectionTitle>
        <CategoryTagline>Find your favorite indulgence below.</CategoryTagline>
        
        <NavigationButton direction="prev" onClick={() => handleSlide('prev')}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </NavigationButton>
        
        <CategorySlider ref={sliderRef}>
          <SliderWrapper style={{ transform: `translateX(-${currentIndex * STEP}px)` }}>
            <StyledLink to="/category/banana-bread">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0s' }} onClick={triggerBounce}>
                <CategoryImage src="/bnanana_bread.jpg" alt="Banana Bread" />
                <CategoryName>BANANA BREAD</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/pastries">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.05s' }} onClick={triggerBounce}>
                <CategoryImage src="/pastries.jpg" alt="Pastries" />
                <CategoryName>PASTRIES</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/donuts">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.1s' }} onClick={triggerBounce}>
                <CategoryImage src="/donuts.jpg" alt="Donuts" />
                <CategoryName>DONUTS</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/puff-pastries">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.15s' }} onClick={triggerBounce}>
                <CategoryImage src="/Puff_pastries.jpg" alt="Puff Pastries" />
                <CategoryName>Puff Pastries</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/sundaes">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.2s' }} onClick={triggerBounce}>
                <CategoryImage src="/Sundae.png" alt="Sundaes" />
                <CategoryName>Sundaes</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/cakes">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.25s' }} onClick={triggerBounce}>
                <CategoryImage src="/cake.jpg" alt="Cakes" />
              <CategoryName>Cakes</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/cupcakes">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.3s' }} onClick={triggerBounce}>
                <CategoryImage src="/cupcake.jpg" alt="Cupcakes" />
                <CategoryName>Cupcakes</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/fondant">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.35s' }} onClick={triggerBounce}>
                <CategoryImage src="/Fondant.jpg" alt="Fondant" />
                <CategoryName>Fondant</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/brownies">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.4s' }} onClick={triggerBounce}>
                <CategoryImage src="/brownies.jpg" alt="Brownies" />
                <CategoryName>Brownies</CategoryName>
              </CategoryCard>
            </StyledLink>
            <StyledLink to="/category/cookies">
              <CategoryCard data-reveal="zoom" style={{ animationDelay: '0.45s' }} onClick={triggerBounce}>
                <CategoryImage src="/cookies.jpg" alt="Cookies" />
                <CategoryName>Cookies</CategoryName>
              </CategoryCard>
            </StyledLink>
          </SliderWrapper>
        </CategorySlider>
        
        <NavigationButton direction="next" onClick={() => handleSlide('next')}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </NavigationButton>
      </CategorySection>

      {/* Quality Section */}
      <QualitySection>
        <QualityContainer>
          <QualityImageWrap data-reveal data-reveal-dir="right">
            <QualityBadge src="/FlourAndFancy_logo.PNG" alt="Flour & Fancy logo" />
            <picture>
              <QualityPhotoMain
                src="/Chocochip_bread.jpg"
                alt="Chocochip bread"
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          </QualityImageWrap>

          <QualityText data-reveal data-reveal-dir="left">
            <QualityHeading data-reveal>Best Quality In Town!</QualityHeading>
            <QualityParagraph data-reveal style={{ animationDelay: '0.1s' }}>
              Since it was established in a home kitchen, Flour & Fancy has been committed to baking truly exquisite goods, ensuring quality and passion in every batch. Every item is crafted with the finest ingredients and a genuine touch of culinary passion, embodying the true essence of delightful home baking.
            </QualityParagraph>
            <OrderButton
              data-reveal
              style={{ animationDelay: '0.2s' }}
              href="https://wa.me/923708025503"
              aria-label="Order Online via WhatsApp"
              onClick={triggerBounce}
            >
              Order Online
            </OrderButton>
          </QualityText>
        </QualityContainer>
      </QualitySection>

      {/* Stats Section under Quality */}
      <StatsSection>
        <StatsContainer ref={statsRef}>
          <StatItem>
            <StatIcon>
              {/* star icon */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15 9 22 9 17 13 19 20 12 16 5 20 7 13 2 9 9 9 12 2" />
              </svg>
            </StatIcon>
            <StatText>
              <StatLabel>Our Experience</StatLabel>
              <StatNumber><AnimatedNumber to={5} start={statsStart} /> Years</StatNumber>
            </StatText>
          </StatItem>
          <StatItem>
            <StatIcon>
              {/* check icon */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </StatIcon>
            <StatText>
              <StatLabel>Completed Orders</StatLabel>
              <StatNumber><AnimatedNumber to={50} start={statsStart} />+</StatNumber>
            </StatText>
          </StatItem>
          <StatItem>
            <StatIcon>
              {/* smile icon */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 15s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9" y2="9" />
                <line x1="15" y1="9" x2="15" y2="9" />
              </svg>
            </StatIcon>
            <StatText>
              <StatLabel>Happy Clients</StatLabel>
              <StatNumber><AnimatedNumber to={100} start={statsStart} />+</StatNumber>
            </StatText>
          </StatItem>
        </StatsContainer>
      </StatsSection>

      {/* Testimonial Section (replaces Customers Feedback) */}
      <TSection>
        <TContainer>
          <THeadingScript>Testimonial</THeadingScript>
          <THeadingMain>OUR CLIENTS SAY!!!</THeadingMain>
          <THeadingLine />

          <TViewport>
            <TTrack
              style={{
                transform: `translateX(-${tIndex * (100 / tPerView)}%)`,
                transition: tEnableTransition ? undefined : 'none',
              }}
              onTransitionEnd={handleTTransitionEnd}
            >
              {Array.from({ length: tLen * 3 }, (_, i) => testimonials[i % tLen]).map((item, idx) => {
                const activeGlobalIdx = tIndex + (tPerView === 3 ? 1 : 0);
                const isActive = idx === activeGlobalIdx;
                const flexBasis = tPerView === 1 ? '100%' : `calc(${100 / tPerView}% - 1rem)`;
                return (
                  <TCard key={idx} active={isActive} style={{ flex: `0 0 ${flexBasis}` }}>
                    <TAvatar src={item.avatar} alt={item.name} />
                    <TCardBody>
                      <TClientName>{item.name}</TClientName>
                      <TReviewText>{item.text}</TReviewText>
                    </TCardBody>
                  </TCard>
                );
              })}
            </TTrack>
          </TViewport>

          <TPagination>
            {Array.from({ length: tPageCount }).map((_, i) => (
              <TPaginationDot
                key={i}
                active={i === (tIndex % tLen)}
                onClick={() => handleSlideChange(i)}
                aria-label={`Go to testimonials page ${i + 1}`}
              />
            ))}
          </TPagination>
        </TContainer>
      </TSection>

      {/* Contact Us Section removed */}
    </>
  );
};

export default HomePage;